import React, { useEffect } from "react";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import Stack from "@mui/joy/Stack";
import { Alert, Button, CircularProgress, Box } from "@mui/joy";

interface ConfirmationModalProps {
  open: boolean;
  title: string;
  message?: string | React.ReactNode;
  actionLabel?: string;
  cancelLabel?: string;
  onAction: () => Promise<void> | void;
  onClose: () => void;
  loading?: boolean;
  spinner?: React.ReactNode;
  // A caller's failed onAction has nowhere else visible to report to: the
  // modal stays open (callers deliberately don't close it on error, so the
  // user can retry), which puts the page underneath behind this modal's
  // backdrop. A page-level error banner is therefore invisible for as long
  // as this modal is open, and gets cleared if the user closes it before
  // ever seeing it. Render the message here instead.
  error?: string | null;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  title,
  message = "Are you sure?",
  actionLabel = "Confirm",
  cancelLabel = "Cancel",
  onAction,
  onClose,
  loading = false,
  spinner,
  error,
}) => {
  useEffect(() => {
    if (!open) {
      // Parent should handle resetting any loading state
    }
  }, [open]);

  const handleActionClick = async () => {
    await onAction();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog className="modal">
        <DialogTitle>{title}</DialogTitle>
        <form onSubmit={handleActionClick}>
          <Stack spacing={2}>
            {typeof message === "string" ? (
              <FormControl>
                <FormLabel
                  className="formLabel"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {message}
                </FormLabel>
              </FormControl>
            ) : (
              <Box>{message}</Box>
            )}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                {spinner ? spinner : <CircularProgress size="sm" />}
              </Box>
            )}
            {error && (
              <Alert color="danger" variant="soft" role="alert">
                {error}
              </Alert>
            )}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="solid"
                color="neutral"
                onClick={onClose}
                disabled={loading}
              >
                {cancelLabel}
              </Button>
              <Button
                variant="solid"
                color="primary"
                type="button"
                disabled={loading}
                onClick={handleActionClick}
              >
                {actionLabel}
              </Button>
            </Box>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default ConfirmationModal;
